/**
 * Iter-216 · Statement Format Editor — inline drawer inside the Financial
 * Statements tab. Lets users rename buckets (BS / P&L / CF) and hide entire
 * sections. Persists via PATCH /financial-statements/config.
 *
 * The editor doesn't need to know the exact bucket names — it fetches the
 * live BS/P&L payload and offers a per-bucket rename + hide toggle.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Settings2, Save } from "lucide-react";

const _BS_BUCKETS = [
  "equity_share_capital", "reserves_surplus", "long_term_borrowings",
  "deferred_tax_liabilities", "long_term_provisions", "short_term_borrowings",
  "trade_payables", "other_current_liabilities", "short_term_provisions",
  "fixed_assets_tangible", "fixed_assets_intangible", "capital_wip",
  "noncurrent_investments", "long_term_loans_advances",
  "current_investments", "inventories", "trade_receivables",
  "cash_bank", "short_term_loans_advances", "other_current_assets",
];
const _PL_BUCKETS = [
  "revenue_from_operations", "other_income", "cost_of_materials",
  "changes_in_inventories", "employee_benefit_expense", "finance_costs",
  "depreciation_amortisation", "other_expenses",
];
const _CF_SECTIONS = ["operating", "investing", "financing"];

function BucketList({ items, renames, hidden, setRenames, setHidden, testidPrefix }) {
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-list`}>
      {items.map((k) => (
        <div key={k} className="grid grid-cols-[1fr_auto] gap-3 items-center border-b border-border/40 py-2">
          <div>
            <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">{k}</div>
            <Input
              value={renames[k] || ""}
              placeholder={k.replace(/_/g, " ")}
              onChange={(e) => setRenames({ ...renames, [k]: e.target.value })}
              className="h-8 rounded-sm text-sm"
              data-testid={`${testidPrefix}-rename-${k}`}
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Show</span>
            <Switch
              checked={!hidden.has(k)}
              onCheckedChange={(v) => {
                const n = new Set(hidden);
                if (v) n.delete(k); else n.add(k);
                setHidden(n);
              }}
              data-testid={`${testidPrefix}-toggle-${k}`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function StatementFormatEditor({ open, onOpenChange, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [bsRenames, setBsRenames] = useState({});
  const [plRenames, setPlRenames] = useState({});
  const [cfRenames, setCfRenames] = useState({});
  const [bsHidden, setBsHidden] = useState(new Set());
  const [plHidden, setPlHidden] = useState(new Set());
  const [cfHidden, setCfHidden] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/financial-statements/config").then((r) => {
      const d = r.data || {};
      setCfg(d);
      setBsRenames(d.bs_renames || {});
      setPlRenames(d.pl_renames || {});
      setCfRenames(d.cf_renames || {});
      setBsHidden(new Set(d.bs_hidden_buckets || []));
      setPlHidden(new Set(d.pl_hidden_buckets || []));
      setCfHidden(new Set(d.cf_hidden_buckets || []));
    });
  }, [open]);

  const stripEmpty = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => (v || "").trim()));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/financial-statements/config", {
        bs_renames: stripEmpty(bsRenames),
        pl_renames: stripEmpty(plRenames),
        cf_renames: stripEmpty(cfRenames),
        bs_hidden_buckets: [...bsHidden],
        pl_hidden_buckets: [...plHidden],
        cf_hidden_buckets: [...cfHidden],
      });
      toast.success("Format saved · BS · P&L · CF will render with your changes on next refresh");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Save failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl rounded-none overflow-y-auto" data-testid="fs-format-editor">
        <SheetHeader className="border-b pb-3 mb-4">
          <SheetTitle className="flex items-center gap-2 font-heading">
            <Settings2 className="w-4 h-4" /> Statement Format Editor
          </SheetTitle>
          <SheetDescription>
            Rename buckets or hide entire sections. Applies to your Balance Sheet, P&amp;L, Cash Flow, and both PDF / Excel packs.
          </SheetDescription>
        </SheetHeader>
        {!cfg ? <div className="text-muted-foreground text-sm">Loading…</div> : (
          <>
            <Tabs defaultValue="bs">
              <TabsList className="rounded-sm">
                <TabsTrigger value="bs" data-testid="fmt-tab-bs">Balance Sheet</TabsTrigger>
                <TabsTrigger value="pl" data-testid="fmt-tab-pl">P&amp;L</TabsTrigger>
                <TabsTrigger value="cf" data-testid="fmt-tab-cf">Cash Flow</TabsTrigger>
              </TabsList>
              <TabsContent value="bs" className="mt-4">
                <BucketList items={_BS_BUCKETS} renames={bsRenames} hidden={bsHidden}
                            setRenames={setBsRenames} setHidden={setBsHidden}
                            testidPrefix="bs" />
              </TabsContent>
              <TabsContent value="pl" className="mt-4">
                <BucketList items={_PL_BUCKETS} renames={plRenames} hidden={plHidden}
                            setRenames={setPlRenames} setHidden={setPlHidden}
                            testidPrefix="pl" />
              </TabsContent>
              <TabsContent value="cf" className="mt-4">
                <BucketList items={_CF_SECTIONS} renames={cfRenames} hidden={cfHidden}
                            setRenames={setCfRenames} setHidden={setCfHidden}
                            testidPrefix="cf" />
              </TabsContent>
            </Tabs>
            <div className="mt-6 flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="fmt-cancel">Cancel</Button>
              <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="fmt-save">
                <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save Format"}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
